import { useMemo } from "react";
import { Link, useSearchParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../../lib/auth-context.js";
import { usePreferences } from "../../lib/preferences.js";
import { getNetWorth } from "../../lib/api-modules/networth.js";
import { listBills } from "../../lib/api-modules/bills.js";
import { listIncomeEvents } from "../../lib/api-modules/income-events.js";
import { listSpendingTransactions } from "../../lib/api-modules/spending-transactions.js";
import {
  fireNumbers,
  savingsRate,
  timeToFI,
} from "../../lib/util/fire.js";
import {
  accountTaxMix,
  estimateRetirementTaxRatePct,
  fiAssetsCents as computeFiAssets,
  grossUpForTax,
} from "../../lib/util/retirement-plan.js";
import { formatCentsCompact } from "../../lib/util/money.js";
import {
  Card,
  Pill,
  SectionHeader,
  StatCard,
} from "../../components/design-system/index.js";
import { SuggestedPromptsTile } from "../../components/SuggestedPromptsTile.js";
import { PAGE_PROMPTS } from "../../lib/data/page-prompts.js";
import { useDocumentTitle } from "../../lib/util/use-document-title.js";

/**
 * /fire — the marketing-anchor FIRE hub (Phase 13.5).
 *
 * Why this page exists: in the prior IA, FIRE lived as `/dashboard#fire`
 * — a hash anchor on the kitchen-sink dashboard, buried under
 * "Planning". The IA review (DECISIONS.md 2026-05-09 IA review block)
 * elevated FIRE to a top-level group with its own hub page + 7
 * sub-routes. The hub is the page Andre demos for the marketing arc:
 * one hero showing today's FI status + a navigation grid into each
 * sub-page.
 *
 * Share view: `?share=true` masks dollar amounts, leaving percentages
 * + time-to-FI visible. Lets Andre share the hub on social/YouTube
 * without exposing net worth. Sub-pages don't get share-mode in v1
 * (Phase 17+).
 */

const DEFAULT_WITHDRAWAL_RATE_PCT = 4;
const DEFAULT_EXPECTED_RETURN_RATE_PCT = 5;

export function FireHubPage() {
  useDocumentTitle("FIRE");
  const { user } = useAuth();
  const { privacyMode } = usePreferences();
  const [searchParams] = useSearchParams();
  const shareMode = searchParams.get("share") === "true";
  const hideValues = privacyMode || shareMode;

  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const trailingFrom = useMemo(() => {
    const d = new Date(today);
    d.setUTCMonth(d.getUTCMonth() - 12);
    return d.toISOString().slice(0, 10);
  }, [today]);

  const networthQuery = useQuery({
    queryKey: ["networth", "24m"],
    queryFn: () => getNetWorth("24m"),
  });
  const billsQuery = useQuery({
    queryKey: ["bills", { includeArchived: false }],
    queryFn: () => listBills(false),
  });
  const incomeQuery = useQuery({
    queryKey: ["income-events", { from: trailingFrom }],
    queryFn: () => listIncomeEvents({ from: trailingFrom }),
  });
  const spendQuery = useQuery({
    queryKey: ["spending-transactions", { from: trailingFrom, to: today }],
    queryFn: () => listSpendingTransactions({ from: trailingFrom, to: today }),
  });

  const isLoading =
    networthQuery.isLoading ||
    billsQuery.isLoading ||
    incomeQuery.isLoading ||
    spendQuery.isLoading;
  const isError =
    networthQuery.isError ||
    billsQuery.isError ||
    incomeQuery.isError ||
    spendQuery.isError;

  const latestSnapshot = networthQuery.data?.latest;
  const fiAssetsCents = latestSnapshot ? computeFiAssets(latestSnapshot) : 0;
  const byAccountType = latestSnapshot?.by_account_type;
  const assumptions = useMemo(
    () => ({
      withdrawal_rate_pct:
        user?.fire_assumptions?.withdrawal_rate_pct ?? DEFAULT_WITHDRAWAL_RATE_PCT,
      expected_return_rate_pct:
        user?.fire_assumptions?.expected_return_rate_pct ??
        DEFAULT_EXPECTED_RETURN_RATE_PCT,
      annual_spend_override_cents:
        user?.fire_assumptions?.annual_spend_override_cents,
    }),
    [user?.fire_assumptions],
  );

  const computed = useMemo(() => {
    if (isLoading || isError) return null;
    const sr = savingsRate({
      income_events: incomeQuery.data?.income_events ?? [],
      bills: billsQuery.data?.bills ?? [],
      spending_transactions: spendQuery.data?.spending_transactions ?? [],
      today,
      lookback_months: 12,
    });
    const annualSpendCents =
      assumptions.annual_spend_override_cents ?? sr.annualized_spend_cents;
    const taxRatePct = estimateRetirementTaxRatePct({
      annual_spend_cents: annualSpendCents,
      mix: accountTaxMix(byAccountType),
    });
    const numbers = fireNumbers({
      annual_spend_cents: grossUpForTax(annualSpendCents, taxRatePct),
      withdrawal_rate_pct: assumptions.withdrawal_rate_pct,
      lean_spend_multiplier: 0.7,
      fat_spend_multiplier: 2.5,
    });
    const tFI = timeToFI({
      current_net_worth_cents: fiAssetsCents,
      monthly_net_savings_cents: sr.monthly_net_savings_cents,
      annual_return_rate_pct: assumptions.expected_return_rate_pct,
      fire_target_cents: numbers.regular_cents,
      today,
    });
    const fiPct =
      numbers.regular_cents === 0
        ? 0
        : Math.min(
            100,
            Math.max(0, (fiAssetsCents / numbers.regular_cents) * 100),
          );
    return {
      sr,
      annualSpendCents,
      numbers,
      tFI,
      fiPct,
    };
  }, [
    incomeQuery.data,
    billsQuery.data,
    spendQuery.data,
    today,
    isLoading,
    isError,
    assumptions,
    fiAssetsCents,
    byAccountType,
  ]);

  if (!user) return null;

  return (
    <div className="mx-auto max-w-5xl space-y-6 px-4 py-8 sm:px-6">
      <SectionHeader
        title={
          <>
            FIRE{shareMode && <span className="ml-3 text-sm font-normal text-[var(--color-muted)]">share view</span>}
          </>
        }
        sub="Where you stand against your Financial Independence target, and the math behind the path to retire on your terms."
      />

      {/* Hero — current FI status.
       *
       * Empty-state guard: when annual spend resolves to 0 (no bills, no
       * spending transactions, no override set), the FI target is 0 and
       * any positive net worth would otherwise read as "past FI". Show a
       * CTA to add inputs instead — mirrors FireTile's `!hasIncome`
       * empty state but checks the target itself rather than just income,
       * since income > 0 with spend = 0 also produces a meaningless 0
       * target.
       */}
      <Card elevated>
        <p className="text-xs font-medium uppercase tracking-wide text-[var(--color-muted)]">
          Today's status
        </p>
        {isLoading ? (
          <p className="mt-3 text-[var(--color-muted)]">Loading…</p>
        ) : isError ? (
          <p className="mt-3 text-[var(--color-danger)]">
            Couldn't load FIRE inputs.
          </p>
        ) : computed && computed.numbers.regular_cents <= 0 ? (
          <div
            className="mt-3 rounded-md border border-dashed border-[var(--color-border-default)] p-6"
            data-testid="fire-hub-empty"
          >
            <p className="font-medium text-[var(--color-text)]">
              Add your spending to calculate your FI target.
            </p>
            <p className="mt-1 text-sm text-[var(--color-muted)]">
              Hearth needs trailing-12-month bills + transactions (or an
              annual-spend override) to compute your FI number. Without
              spend data the math has no denominator.
            </p>
            <div className="mt-4 flex flex-wrap gap-3 text-sm">
              <Link
                to="/spending"
                className="rounded-md border border-[var(--color-border-default)] px-3 py-1.5 hover:border-[var(--color-accent)] hover:text-[var(--color-accent)]"
              >
                Add spending →
              </Link>
              <Link
                to="/bills"
                className="rounded-md border border-[var(--color-border-default)] px-3 py-1.5 hover:border-[var(--color-accent)] hover:text-[var(--color-accent)]"
              >
                Add bills →
              </Link>
              <Link
                to="/fire/numbers"
                className="rounded-md border border-[var(--color-border-default)] px-3 py-1.5 hover:border-[var(--color-accent)] hover:text-[var(--color-accent)]"
              >
                Set annual-spend override →
              </Link>
            </div>
          </div>
        ) : computed ? (
          <>
            <div className="mt-1 flex flex-wrap items-baseline gap-3">
              <p className="font-display text-5xl font-medium tracking-tight tabular-nums text-[var(--color-text)]">
                {computed.fiPct.toFixed(1)}%
              </p>
              <p className="text-sm text-[var(--color-muted)]">
                of your Regular FI target
                {hideValues
                  ? ""
                  : ` (${formatCentsCompact(computed.numbers.regular_cents, { hidden: false })})`}
              </p>
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              <StatCard
                label="Saving rate (TTM)"
                value={`${(computed.sr.savings_rate_pct ?? 0).toFixed(1)}%`}
                sub="Trailing 12 months"
              />
              <StatCard
                label="Time to Regular FI"
                value={
                  computed.tFI.years_to_fi === null
                    ? "—"
                    : `${computed.tFI.years_to_fi.toFixed(1)} yrs`
                }
                sub={
                  computed.tFI.fi_date
                    ? `Crosses ${computed.tFI.fi_date}`
                    : "Need positive savings to project"
                }
              />
              <StatCard
                label={hideValues ? "Annual spend" : "Annualized spend"}
                value={
                  hideValues
                    ? "—"
                    : formatCentsCompact(computed.annualSpendCents, {
                        hidden: false,
                      })
                }
                sub={`@ ${assumptions.withdrawal_rate_pct}% safe withdrawal`}
              />
            </div>
            {computed.tFI.already_fi && (
              <Pill tone="success" className="mt-4">
                You're past Regular FI — congratulations.
              </Pill>
            )}
          </>
        ) : null}
      </Card>

      {/* Sub-page navigation grid. */}
      <div className="grid gap-4 md:grid-cols-2">
        <HubCard
          to="/fire/numbers"
          title="Numbers"
          sub="Lean / Coast / Barista / Regular / Fat — and Shockingly Simple Math."
        />
        <HubCard
          to="/fire/withdrawal"
          title="Withdrawal modeler"
          sub="Will your savings last? Past markets, random futures and 5 ways of spending."
        />
        <HubCard
          to="/fire/saving-rate"
          title="Saving rate"
          sub="Trailing-12 trend with MoM/YoY deltas + on-track status."
        />
        <HubCard
          to="/fire/contribution-priority"
          title="Where the next dollar goes"
          sub="Ranked Bogleheads investment-order rungs against your YTD."
        />
        <HubCard
          to="/scenarios"
          title="Scenarios"
          sub="Replay saved scenarios — higher inflation, sabbatical, geo-arb."
        />
        <HubCard
          to="/fire/social-security"
          title="Social Security"
          sub="Estimated benefit by claim age and the bridge it provides."
        />
        <HubCard
          to="/fire/geographic-arbitrage"
          title="Geographic arbitrage"
          sub="What FIRE looks like in lower-cost cities."
        />
      </div>

      <SuggestedPromptsTile prompts={PAGE_PROMPTS.fire} />
    </div>
  );
}

function HubCard({
  to,
  title,
  sub,
}: {
  readonly to: string;
  readonly title: string;
  readonly sub: string;
}) {
  return (
    <Link
      to={to}
      className="group rounded-xl border border-[var(--color-border-default)] bg-[var(--color-card)] p-5 transition-colors hover:border-[var(--color-accent)] hover:bg-[var(--color-elevated)]"
    >
      <p className="font-display text-lg font-medium text-[var(--color-text)] group-hover:text-[var(--color-accent)]">
        {title} →
      </p>
      <p className="mt-1 text-sm text-[var(--color-muted)]">{sub}</p>
    </Link>
  );
}
